import React from 'react';
import {
  Stack,
  Text,
  Box,
  Skeleton,
  LinkBox,
  useTheme,
  Button,
  Tooltip,
  Flex,
  HStack,
} from '@chakra-ui/react';
import format from 'date-fns/format';
import { useInView } from 'react-intersection-observer';
import NextImage from 'next/image';
import { useRouter } from '@tanstack/react-router';
import { Link } from '@chakra-ui/next-js';
import _ from 'lodash';
import { gql } from '../../../../__generated__/gql';
import { FeatureTileFragment, UserTileFragment } from '../../../../__generated__/graphql';
import PerfImageFromFile from '../../util/PerfImageFromFile';
import { useThemeColors } from '../../../hooks/useThemeColors';
import Heading from '../../heading';
import { getCommonProps } from '../../../theme/components/button';
import { getEmojisAsArrayFromString } from '../../../../emoji';
import { useAuth } from '../../../hooks/useAuth';
import useFollowUser from '../../../hooks/useFollowUser';
import { useIsBackgroundLight } from '../../../hooks/useContrastHighlight';

gql(/* GraphQL */ `
  fragment UserTile on User {
    ...UserDirectoryItem
    ...UserCore
    amIFollowing
    profileTheme
    areTheyFollowing
    avatarPhotoFile {
      ...PerfImageFromFile
    }
    recs(first: 5, includePromptRecs: true) {
      id
      emoji
    }
  }
`);

type Props = {
  size?: 'lg' | 'sm';
  user: UserTileFragment;
  priority?: boolean;
  skipStars?: boolean;
};

function UserTile({ user, size = 'sm', priority = false, skipStars }: Props) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: '2000px 0px 200px',
  });

  const href = `/u/${user.username}`;

  const themeColors = useThemeColors();

  const auth = useAuth();
  const router = useRouter();

  const colors = useThemeColors(user.profileTheme);

  // const isToday =
  //   isAfter(sub(new Date(), { days: 1 }), new Date(feature.date)) &&
  //   isBefore(add(new Date(), { days: 1 }), new Date(feature.date));

  const dimension = 150;

  const { followUser, unfollowUser } = useFollowUser({ user });

  const isLight = useIsBackgroundLight(colors.background);

  return (
    <Stack
      role="group"
      spacing="12px"
      ref={ref}
      border="1px dashed"
      borderColor={colors.highlight}
      backgroundColor={colors.background}
      overflow="hidden"
      justifyContent="center"
      height="100%"
    >
      <LinkBox as={Link} href={href} cursor="pointer" width={dimension} alignSelf="center">
        <Stack justifyContent="center" px="4px" pb="12px" width={dimension} alignSelf="center">
          <Heading
            variant="XL"
            theme={user.profileTheme}
            minSize={6}
            maxSize={25}
            strokeWidth={1}
            fitToContainer
            color={colors.titleBackground}
            className=" text-center text-[30px] pt-4"
          >
            <span className="whitespace-nowrap">@{_.truncate(user.username, { length: 15 })}</span>
            {/* {(user.firstName || user.username) && (
              <Box as="span" whiteSpace="nowrap">
                {user.firstName ? user.firstName : `@${user.username}`}
              </Box>
            )}
            {user.lastName && (
              <Box as="div" whiteSpace="nowrap">
                {user.lastName}
              </Box>
            )} */}
          </Heading>
          <Tooltip label="Recent recs.">
            <Text
              textStyle="brand.actionSm"
              color={colors.main}
              textAlign="center"
              whiteSpace="nowrap"
              fontSize="16px"
              opacity={user?.recs?.length ? 1 : 0.4}
            >
              {user?.recs?.length
                ? getEmojisAsArrayFromString(
                    user?.recs?.reduce((acc, r) => `${acc}${r.emoji?.trim()}`, ''),
                  )
                    .slice(0, 5)
                    .join(' ')
                    .trim()
                : 'No recs yet..'}
            </Text>
          </Tooltip>
        </Stack>
        <Flex width={dimension} maxHeight="fit-content" justifyContent="center">
          {user.avatarPhotoFile ? (
            <PerfImageFromFile
              file={user.avatarPhotoFile}
              backgroundColor={colors.background}
              useFallbackAvatarSrc
              alt="profile photo"
              quality={80}
              priority={inView}
              width={dimension}
              height={dimension}
              style={{
                width: `${dimension}px`,
                height: `${dimension}px`,
                objectFit: 'cover',
                objectPosition: 'top',
              }}
            />
          ) : (
            <img
              src={`/default-profile-photo-${
                isLight || !(themeColors.main === '#ffffff' || themeColors.main === 'white')
                  ? 'blue'
                  : 'white'
              }-small.png`}
              alt="profile photo"
              quality={80}
              width={dimension}
              height={dimension}
              style={{
                width: `${dimension}px`,
                height: `${dimension}px`,
                maxHeight: '175px',
                objectFit: 'cover',
                objectPosition: 'top',
              }}
            />
          )}
        </Flex>
      </LinkBox>

      <HStack width={dimension} alignSelf="center" pb="6px">
        <Button
          width="60%"
          height="fit-content"
          p="3px"
          isDisabled={auth.user?.id === user.id}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (auth.user) {
              if (user.amIFollowing) {
                unfollowUser();
              } else {
                followUser();
              }
            } else {
              router.navigate({ to: '/sign-in' });
            }
          }}
          {...getCommonProps({
            color: colors.background,
            backgroundColor: colors.highlight,
            border: '1px solid transparent',
          })}
        >
          {auth.user && user.amIFollowing
            ? 'Following'
            : user.id === auth.user?.id
            ? `That's you!`
            : 'Follow'}
        </Button>
        <Button
          as={Link}
          href={href}
          width="40%"
          height="fit-content"
          p="3px"
          {...getCommonProps({
            color: colors.highlight,
            backgroundColor: colors.background,
            borderWidth: '1px',
            borderColor: colors.highlight,
          })}
        >
          View
        </Button>
      </HStack>
    </Stack>
  );
}

const FallBackImage = ({ withSkeleton }: { withSkeleton?: boolean }) => {
  return (
    <Box
      display="flex"
      width="100%"
      objectFit="cover"
      aspectRatio="1 / 1"
      objectPosition="0 0"
      justifyContent="center"
      alignItems="center"
      {...(!withSkeleton
        ? {
            backgroundColor: 'brand.highlight',
            // border: '1px solid',
            // borderColor: 'brand.highlight',
          }
        : {})}
    >
      {withSkeleton && (
        <Skeleton height="100%" width="100%" startColor="brand.highlight" endColor="#0096FF" />
      )}
    </Box>
  );
};

export default UserTile;
